import { Alert, Button, Modal, Text } from '@viapeople/components'
import { AlertTriangle } from 'lucide-react'
import { useEffect, useState } from 'react'

export interface RemoveParticipantDialogProps {
	isOpen: boolean
	onClose: () => void
	onConfirm: (mode: 'display' | 'hide') => void
	participantNames: string[]
	hasData: boolean
	cycleStatus: 'draft' | 'in-progress' | 'ready-for-launch' | 'completed'
	cycleName: string
	isLoading: boolean
	// Mirrors the per-org `participant-soft-delete` feature flag (sourced from the
	// preDeleteCheck response). When false AND hasData=true, the dialog renders a
	// refusal panel — the operation is server-blocked and the FE refuses up-front
	// with a clear message instead of pretending to offer modes.
	softDeleteEnabled: boolean
}

const MAX_VISIBLE_NAMES = 5

const RADIO_OPTIONS: { value: 'display' | 'hide'; label: string; description: string }[] = [
	{
		value: 'display',
		label: 'Keep data visible',
		description:
			'Completed responses remain accessible. Evaluation appears in search with a strikethrough name. Unfinished tasks are deleted.',
	},
	{
		value: 'hide',
		label: 'Hide all data',
		description:
			'All responses are hidden. Evaluation is removed from search results. Data is preserved internally and can be restored by support.',
	},
]

export const RemoveParticipantDialog = ({
	isOpen,
	onClose,
	onConfirm,
	participantNames,
	hasData,
	cycleStatus,
	cycleName,
	isLoading,
	softDeleteEnabled,
}: RemoveParticipantDialogProps) => {
	const [selectedMode, setSelectedMode] = useState<'display' | 'hide' | null>(null)

	useEffect(() => {
		if (isOpen) setSelectedMode(null)
	}, [isOpen])

	const count = participantNames.length
	const title = `Remove ${count} ${count === 1 ? 'participant' : 'participants'} from ${cycleName}`

	const visibleNames = participantNames.slice(0, MAX_VISIBLE_NAMES)
	const hiddenCount = participantNames.length - visibleNames.length

	// Refusal mode: org doesn't have soft-delete enabled but the selection includes
	// participants with submitted data. Server will refuse — show that up-front.
	const isRefusing = !softDeleteEnabled && hasData

	const isConfirmDisabled = isLoading || (hasData && selectedMode === null)

	const handleConfirm = () => {
		onConfirm(selectedMode ?? 'display')
	}

	return (
		<Modal isOpen={isOpen} onClose={onClose} title={title} className='min-w-[500px]'>
			<div className='flex flex-col gap-4'>
				{cycleStatus === 'completed' && (
					<Alert
						variant='info'
						icon={AlertTriangle}
						message='This cycle is completed. Removing a participant will affect historical data.'
					/>
				)}

				<div className='flex flex-col gap-1'>
					{visibleNames.map((name) => (
						<Text key={name} variant='small' className='text-neutral-800'>
							{name}
						</Text>
					))}
					{hiddenCount > 0 && (
						<Text variant='small' className='text-neutral-500'>
							and {hiddenCount} more
						</Text>
					)}
				</div>

				{isRefusing ? (
					<Alert
						variant='error'
						icon={AlertTriangle}
						message='Cannot remove the selected participants because they have submitted evaluation data. Soft-delete is not enabled for this organization.'
					/>
				) : !hasData ? (
					<Text variant='small' className='text-neutral-700'>
						This participant has no evaluation data. They will be removed from the cycle.
					</Text>
				) : (
					<div className='flex flex-col gap-2'>
						{RADIO_OPTIONS.map((option) => (
							<label
								key={option.value}
								aria-label={option.label}
								className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
									selectedMode === option.value
										? 'border-primary-500 bg-primary-50'
										: 'border-neutral-200 bg-white hover:border-neutral-300'
								}`}>
								<input
									type='radio'
									name='removal-mode'
									value={option.value}
									checked={selectedMode === option.value}
									onChange={() => setSelectedMode(option.value)}
									className='mt-0.5 accent-primary-600'
								/>
								<div>
									<Text variant='small' weight='semibold'>
										{option.label}
									</Text>
									<Text variant='xsmall' className='text-neutral-500 mt-0.5'>
										{option.description}
									</Text>
								</div>
							</label>
						))}
					</div>
				)}

				<div className='flex justify-end gap-2 pt-2 border-t border-neutral-100'>
					{isRefusing ? (
						<Button variant='secondary' onClick={onClose}>
							Close
						</Button>
					) : (
						<>
							<Button variant='secondary' onClick={onClose} disabled={isLoading}>
								Cancel
							</Button>
							<Button variant='destructive' onClick={handleConfirm} disabled={isConfirmDisabled} isLoading={isLoading}>
								Remove Participant
							</Button>
						</>
					)}
				</div>
			</div>
		</Modal>
	)
}
